import React, { useEffect, useState } from "react";
import styles from "./TicketProcessingView.module.scss";
import ConditionalRender from "../../reusable-components/ConditionalRender";
import CustomTableWithPagination from "../../reusable-components/CustomTable";
import {
  fieldNames,
  TABLE_PAGESIZE_OPTIONS,
  ticketProcessingColumns,
  ticketProcessingConstants,
} from "./constants";
import { InputfieldTypes } from "../../enums/InputFieldAttributesEnums";
import { disableFutureDates } from "../../utils/disabledDateUtil";
import TNButton from "../../reusable-components/TNButton";
import { ButtonShape, ButtonSize } from "../../enums/ButtonAttributesEnums";
import Form from "../Form";
import InputField from "../../reusable-components/TNInputField";
import FilterIcon from "../../assets/images/filterIcon.svg";
import CalenderIcon from "../../assets/images/calendar-icon.svg";
import TicketProcessingFilter from "./TicketProcessingFilter";
import TicketProcessingServices from "../../services/ticketProcessing.service";
import { useTicketProcessingFilterContext } from "../../context/TicketProcessingFilterContext";
import { DateFormat, TimeTypes } from "../../enums/dateTimeFormat";
import { appRoutes } from "../../constants/appRoutes";
import moment from "moment";
import { message } from "antd";
import { useHistory } from "react-router-dom";
import RangePickerFilter from "./RangePickerFilter";
import InvoiceDetailsModal from "./InvoiceDetailsModal";
import GenerateSuccessModal from "./GenerateSuccessModal";
import {
  FetchInvoiceMetaType,
  GetProcessContentType,
} from "../../Types/ticketProcessingTypes";
import TNLoader from "../../reusable-components/TNLoader";
import { storageKeys } from "../../constants/storageKeys";
import RolesServices from "../../services/roles.services";
import { UserRoleActions } from "../../enums/UserRolesEnum";
import JobDatabaseService from "../../services/jobDatabase.service";
import { ApiResponse } from "../../enums/ApiResponse";

export interface DateFilterProps {
  startDate?: string;
  endDate?: string;
}

const TicketProcessingView = () => {
  const {
    EMPTY_TABLE_MSG,
    NO_TICKETS_FOUND,
    FILTER_BTN_CLASS,
    GENERATE_INVOICE_BTN_TEXT,
    FILTER_BUTTON_TEXT,
    FILTER_ICON_ALT,
    USER_DETAILS_KEY,
    ID_VALUE_KEY,
    INVOICE_ERROR_MSG,
    INVOICE_ERROR_CLASSNAME,
  } = ticketProcessingConstants;

  const { GetPaginationDefaultMeta } = JobDatabaseService();
  const navigate = useHistory();

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isInvoiceDetailsModalOpen, setIsInvoiceDetailsModalOpen] =
    useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(
    Number(
      sessionStorage.getItem(storageKeys.TICKET_PROCESSING_PAGESIZE) ?? 10,
    ),
  );
  const [pageSizeChanged, setPageSizeChanged] = useState<boolean>(false);
  const [invoiceMeta, setInvoiceMeta] = useState<GetProcessContentType>({});
  const {
    processingFilters,
    updateProcessingFilters,
    selectedRows,
    updateSelectedRows,
  } = useTicketProcessingFilterContext();

  const userDetails = localStorage.getItem(USER_DETAILS_KEY);
  const userDetailsJson = JSON.parse(userDetails);
  const userId = userDetailsJson?.[ID_VALUE_KEY];
  const userRoleInfo = userDetailsJson?.[storageKeys.USER_ROLE_KEY];
  const userRoleProfileInfo = userDetailsJson?.[storageKeys.USER_PROFILE_KEY];

  const { FetchUserRoleDetails } = RolesServices();
  const { data: roleDetails } = FetchUserRoleDetails(userRoleInfo);
  const { data: defaultData, isFetching: gettingDefaults } =
    GetPaginationDefaultMeta();

  const managerReviewTabAccess = !!userRoleProfileInfo
    ? roleDetails?.[0]?.otherData?.[4]?.multiPannel?.[1]?.selectedOptions?.includes(
        UserRoleActions.MANAGER_REVIEW,
      )
    : true;

  const archivedTabViewAccess = !!userRoleProfileInfo
    ? roleDetails?.[0]?.otherData?.[4]?.multiPannel?.[1]?.selectedOptions?.includes(
        UserRoleActions.ARCHIVED_VIEW,
      )
    : true;

  const archivedTabEditAccess = !!userRoleProfileInfo
    ? roleDetails?.[0]?.otherData?.[4]?.multiPannel?.[1]?.selectedOptions?.includes(
        UserRoleActions.ARCHIVED_EDIT,
      )
    : true;

  const invoiceManagementAccess = !!userRoleProfileInfo
    ? roleDetails?.[0]?.otherData?.[4]?.multiPannel?.[2]?.selectedOptions
        ?.length > 0
    : false;

  const { FetchTicketProcessingList, FetchInvoiceMeta } =
    TicketProcessingServices();
  const {
    data,
    refetch,
    isLoading,
    isFetching,
    isRefetching,
    isSuccess: listFetchedSuccess,
  } = FetchTicketProcessingList(
    {
      companyId: processingFilters?.customerName,
      jobId: processingFilters?.jobName,
      truckId: processingFilters?.truckNumber,
      startDate: processingFilters?.startDate,
      endDate: processingFilters?.endDate,
      userId: userId,
      page: currentPage,
      perPage: pageSize,
    },
    pageSizeChanged,
    !!sessionStorage.getItem(storageKeys.TICKET_PROCESSING_PAGESIZE),
  );

  const { mutateAsync: fetchInvoiceMeta, isPending: invoiceMetaPending } =
    FetchInvoiceMeta();

  const handleSetDateRange: DateFilterProps = ({ startDate, endDate }) => {
    setCurrentPage(1);
    updateProcessingFilters({
      ...processingFilters,
      ...(!!startDate
        ? {
            startDate: startDate,
            endDate: endDate,
          }
        : {
            startDate: undefined,
            endDate: undefined,
          }),
    });
  };

  const handleOpenFilterDrawer = () => {
    setIsFilterOpen(true);
  };

  const handleCloseFilterDrawer = () => {
    setIsFilterOpen(false);
  };

  const handleInvoiceDetailsOpen = async (
    dateRanges: FetchInvoiceMetaType[],
  ) => {
    const invoiceDetails = await fetchInvoiceMeta(dateRanges);
    setInvoiceMeta(invoiceDetails?.data);
    setIsInvoiceDetailsModalOpen(true);
  };

  const handleInvoiceDetailsClose = () => {
    setIsInvoiceDetailsModalOpen(false);
  };

  const handleRowSelectionChange = (selectedRowKeys, selectedRows) => {
    const updatedRows = selectedRows?.map((row) => {
      const dateRange = row?.dateRange;
      return {
        ...row,
        rowId: `${dateRange?.startDate}_${dateRange?.endDate}`,
      };
    });
    updateSelectedRows(updatedRows);
  };

  const rowSelectionProps = {
    hideSelectAll: true,
    onChange: handleRowSelectionChange,
    selectedRowKeys: selectedRows?.map(
      (item) => `${item?.dateRange?.startDate}_${item?.dateRange?.endDate}`,
    ),
  };

  const handleNavigateViewTicket = (row) => {
    const dateData = {
      ...row?.dateRange,
    };
    const id = encodeURIComponent(
      `${row?.dateRange?.startDate}_${row?.dateRange?.endDate}`,
    );
    navigate.push(`${appRoutes.VIEW_TICKET_PROCESSING_REVIEW}${id}`, dateData);
  };

  const handleCurrentPage = (page: number) => {
    setCurrentPage(page);
  };

  const handlePagination = (page: number) => handleCurrentPage(page);

  const handlePaginationPageSize = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
    setPageSizeChanged(true);
    try {
      sessionStorage.setItem(storageKeys.TICKET_PROCESSING_PAGESIZE, size);
    } catch (err) {
      console.error("Err", err);
    }
  };

  const handleGenerateInvoiceAction = () => {
    const approvedCount = selectedRows?.reduce(
      (accumulator, currentValue) => accumulator + currentValue?.approvedCount,
      0,
    );

    if (!!approvedCount) {
      const dateRanges = selectedRows?.map((item) => item?.dateRange);
      handleInvoiceDetailsOpen(dateRanges);
    } else {
      message.error({
        content: INVOICE_ERROR_MSG,
        className: INVOICE_ERROR_CLASSNAME,
      });
      updateSelectedRows([]);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [processingFilters, pageSize]);

  useEffect(() => {
    refetch();
  }, [currentPage, pageSize, processingFilters]);

  useEffect(() => {
    if (
      !!defaultData?.message &&
      defaultData?.message === ApiResponse.SUCCESS
    ) {
      setPageSize(defaultData?.data?.processingDefaultPageSize);
      sessionStorage.setItem(
        storageKeys.TICKET_PROCESSING_PAGESIZE,
        defaultData?.data?.processingDefaultPageSize,
      );
    }
  }, [defaultData]);

  useEffect(() => {
    if (listFetchedSuccess && pageSizeChanged) {
      setPageSizeChanged(false);
    }
  }, [listFetchedSuccess, pageSizeChanged]);

  return (
    <>
      <TNLoader
        isLoading={
          invoiceMetaPending ||
          isLoading ||
          isFetching ||
          isRefetching ||
          gettingDefaults
        }
      />
      <div className={styles.headerSection}>
        <TicketProcessingFilter
          isOpen={isFilterOpen}
          handleClose={handleCloseFilterDrawer}
        />
        <InvoiceDetailsModal
          isOpen={isInvoiceDetailsModalOpen}
          handleClose={handleInvoiceDetailsClose}
          startDate={selectedRows?.[0]?.dateRange?.startDate}
          endDate={selectedRows?.[0]?.dateRange?.endDate}
          isRange={selectedRows?.length > 1}
          dateRanges={selectedRows?.map((item) => item?.dateRange)}
          handleSelectedUpdate={() => updateSelectedRows([])}
          invoiceMeta={invoiceMeta}
          hasInvoiceAccess={invoiceManagementAccess}
        />
        <div className={styles.leftContainer}>
          <Form>
            {() => {
              return (
                <RangePickerFilter handleSetDateRange={handleSetDateRange} />
              );
            }}
          </Form>
          <TNButton
            className={styles.filterButton}
            filterButton
            shape={ButtonShape.DEFAULT}
            size={ButtonSize.LARGE}
            text="Filter"
            afterTitle={true}
            icon={<img src={FilterIcon} />}
            onClick={handleOpenFilterDrawer}
          />
        </div>
        <div className={styles.rightContainer}>
          <ConditionalRender
            visible={
              managerReviewTabAccess ||
              archivedTabEditAccess ||
              archivedTabEditAccess
            }
          >
            <ConditionalRender visible={data?.data?.length}>
              <TNButton
                shape={ButtonShape.DEFAULT}
                size={ButtonSize.LARGE}
                text={GENERATE_INVOICE_BTN_TEXT}
                onClick={handleGenerateInvoiceAction}
                disabled={!!!selectedRows?.length}
              />
            </ConditionalRender>
          </ConditionalRender>
        </div>
      </div>
      <div className={styles.tableSection}>
        <ConditionalRender
          visible={data?.data?.length}
          hideFallback={isLoading || isFetching || isRefetching}
          fallback={
            <div className={styles.emptyContainer}>
              <p>
                {processingFilters?.customerName &&
                processingFilters?.jobName &&
                processingFilters?.truckNumber
                  ? EMPTY_TABLE_MSG
                  : NO_TICKETS_FOUND}
              </p>
            </div>
          }
        >
          <CustomTableWithPagination
            bordered={false}
            isClickableTable
            rowKey={(record) =>
              `${record?.dateRange?.startDate}_${record?.dateRange?.endDate}`
            }
            rowSelection={
              managerReviewTabAccess ||
              archivedTabViewAccess ||
              archivedTabEditAccess
                ? rowSelectionProps
                : undefined
            }
            rowClassName={styles.ticketTableRows}
            columns={ticketProcessingColumns}
            dataSource={data?.data ?? []}
            totalData={data?.meta?.totalRecords}
            currentPage={currentPage}
            pageSize={pageSize}
            pageSizeOptions={TABLE_PAGESIZE_OPTIONS}
            handleChange={handlePagination}
            handleSizeChangerChange={handlePaginationPageSize}
            handleSelectedRow={handleNavigateViewTicket}
          />
        </ConditionalRender>
      </div>
    </>
  );
};

export default TicketProcessingView;
